import ReactMarkdown from 'react-markdown';
import { useNavigate } from 'react-router-dom';
import { App } from 'antd';
import {
  RotateCcw,
  TrendingUp,
  MessageCircle,
  Lightbulb,
  Target,
  BookOpen,
  Briefcase,
  User,
  Brain,
  ChevronLeft,
} from 'lucide-react';
import type { Message } from '../types';
import { auth } from '../services/auth';
import type { InterviewConfig, InterviewReport } from '../types';
import { logo } from '../assets/images';

interface InterviewReportProps {
  messages: Message[];
  report: InterviewReport;
  onRestart: () => void;
  config?: InterviewConfig;
  showBackButton?: boolean;
  onBack?: () => void;
}

// 默认用户头像
const DEFAULT_USER_AVATAR =
  'https://api.dicebear.com/7.x/avataaars/svg?seed=Felix&backgroundColor=b6e3f4';

export default function InterviewReport({
  messages,
  report,
  onRestart,
  config,
  showBackButton = false,
  onBack,
}: InterviewReportProps) {
  const navigate = useNavigate();
  const { message: antdMessage } = App.useApp();

  // 获取用户头像
  const userAvatar = auth.getUser()?.avatar || DEFAULT_USER_AVATAR;

  // 进入薪酬沟通
  const handleGoToSalaryNegotiation = () => {
    // 检查综合评分是否低于60分
    if (report?.totalScore < 60) {
      antdMessage.warning('面试评分过低，无法进入谈薪环节');
      return;
    }

    navigate('/loading', {
      state: {
        interviewConfig: config,
        report,
        mode: 'salary', // 标记为谈薪模式
      },
    });
  };

  // 使用传入的报告数据或默认值
  const scores = report?.dimensions || {
    expression: 80,
    technical: 78,
    logic: 80,
    communication: 85,
    professionalism: 83,
    learning: 81,
    overall: 81,
  };

  const totalScore = report?.totalScore;

  // 优势分析
  const strengths = report?.strengths || [
    '基础概念掌握扎实，回答思路清晰有条理',
    '沟通态度积极主动，表达流畅自然有亲和力',
    '具备一定的项目实践经验，能将理论应用到实际',
    '对行业和技术趋势有基本了解，有持续学习的意识',
  ];

  // 改进建议
  const suggestions = report?.suggestions || [
    {
      category: '表达能力',
      content:
        '建议在回答问题时采用"总-分-总"的结构，先给出核心观点，再用具体案例支撑，最后总结强调。',
    },
    {
      category: '技术深度',
      content:
        '技术问题的回答可以更加深入，不仅要说明"做了什么"，更要解释"为什么这样做"。',
    },
    {
      category: '沟通节奏',
      content:
        '回答前可以稍作停顿整理思路，保持适中的语速。适当的沉默比匆忙回答更能体现沉稳。',
    },
    {
      category: '项目经验',
      content:
        '建议使用 STAR 法则（情境-任务-行动-结果）来描述项目经历，量化你的贡献和成果。',
    },
  ];

  // 详细分析
  const detailedAnalysis = report?.detailedAnalysis || {
    expression: '你的表达能力整体良好，语言流畅，能够清晰地传达想法。',
    technical: '技术基础扎实，对核心概念有较好的理解。',
    logic: '逻辑思维较为清晰，能够按照一定的条理回答问题。',
    communication: '沟通态度积极，能够认真倾听问题并给出回应。',
    professionalism: '整体职业素养良好，展现出对面试的重视和对岗位的尊重。',
    learning: '展现出较强的学习意愿和成长潜力，对新技术持开放态度。',
  };

  // 下一步行动
  const nextSteps = report?.nextSteps || [
    '针对面试中回答不够理想的问题，进行复盘和总结',
    '练习使用 STAR 法则描述项目经历，准备 3-5 个不同场景的案例',
    '保持学习的习惯，关注行业动态，提升技术深度和广度',
  ];

  // 获取面试官信息
  const interviewerAvatar = config?.interviewer?.avatar;
  const interviewerName = config?.interviewer?.name || '面试官';

  // 根据评分获取颜色和等级信息 - 与简历评估结果页保持一致
  const getScoreInfo = (score: number) => {
    if (score >= 90) {
      return {
        color: '#22c55e',
        gradient: 'from-green-400 via-green-500 to-green-600',
        label: '卓越',
        glow: 'shadow-green-500/30',
        textColor: 'text-green-400',
      };
    }
    if (score >= 80) {
      return {
        color: '#f59e0b',
        gradient: 'from-amber-400 via-amber-500 to-amber-600',
        label: '优秀',
        glow: 'shadow-amber-500/30',
        textColor: 'text-amber-400',
      };
    }
    if (score >= 70) {
      return {
        color: '#eab308',
        gradient: 'from-yellow-400 via-yellow-500 to-yellow-600',
        label: '良好',
        glow: 'shadow-yellow-500/30',
        textColor: 'text-yellow-400',
      };
    }
    if (score >= 60) {
      return {
        color: '#f97316',
        gradient: 'from-orange-400 via-orange-500 to-orange-600',
        label: '及格',
        glow: 'shadow-orange-500/30',
        textColor: 'text-orange-400',
      };
    }
    return {
      color: '#ef4444',
      gradient: 'from-red-400 via-red-500 to-red-600',
      label: '待提升',
      glow: 'shadow-red-500/30',
      textColor: 'text-red-400',
    };
  };

  // 根据分数获取进度条颜色 - 与h5端保持一致
  const getScoreBarColor = (score: number) => {
    if (score >= 90) return 'bg-green-500';
    if (score >= 80) return 'bg-amber-500';
    if (score >= 70) return 'bg-yellow-500';
    if (score >= 60) return 'bg-orange-500';
    return 'bg-red-500';
  };

  // 行为表现指标颜色 - higherIsBetter=true 时数值越高越好, false 时越低越好
  const getBehaviorColor = (value: number, higherIsBetter = true) => {
    if (higherIsBetter) {
      if (value >= 80) return 'text-emerald-400';
      if (value >= 60) return 'text-amber-400';
      return 'text-red-400';
    }
    if (value <= 30) return 'text-emerald-400';
    if (value <= 60) return 'text-amber-400';
    return 'text-red-400';
  };

  const scoreInfo = getScoreInfo(totalScore);

  // 维度配置
  const dimensions = [
    {
      label: '表达能力',
      score: scores.expression,
      icon: MessageCircle,
      key: 'expression',
    },
    {
      label: '技术能力',
      score: scores.technical,
      icon: Brain,
      key: 'technical',
    },
    { label: '逻辑思维', score: scores.logic, icon: Lightbulb, key: 'logic' },
    {
      label: '沟通协作',
      score: scores.communication,
      icon: User,
      key: 'communication',
    },
    {
      label: '职业素养',
      score: scores.professionalism,
      icon: Briefcase,
      key: 'professionalism',
    },
    {
      label: '学习能力',
      score: scores.learning,
      icon: BookOpen,
      key: 'learning',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0B0F19] via-[#0F172A] to-[#1E293B] p-4 md:p-8 pt-24">
      <div className="max-w-6xl mx-auto" style={{ marginTop: '60px' }}>
        {/* 返回按钮 */}
        {showBackButton && onBack && (
          <button
            onClick={onBack}
            className="mb-0 flex items-center gap-2 text-slate-400 hover:text-white transition-colors cursor-pointer"
          >
            <ChevronLeft className="w-5 h-5" />
            <span style={{ position: 'relative', left: '-5px' }}>返回</span>
          </button>
        )}

        {/* 顶部装饰 */}
        <div className="relative mb-8">
          <div className="absolute inset-0 bg-gradient-to-r from-amber-600/20 via-emerald-600/20 to-amber-600/20 blur-3xl" />
          <div className="relative text-center pt-0 pb-8">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-2xl overflow-hidden mb-4">
              <img
                src={logo}
                alt="面面帮"
                className="w-full h-full object-contain"
              />
            </div>
            <h1 className="text-4xl font-bold text-white mb-2">
              面面帮 · 面试复盘报告
            </h1>
            <p className="text-slate-400">
              AI 赋能，面试无忧 · 专业建议 · 助力成长
            </p>
          </div>
        </div>

        {/* 总分卡片 */}
        <div className="relative bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-3xl p-8 mb-6 border border-slate-700/50 shadow-2xl">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-500 via-emerald-500 to-amber-500 rounded-t-3xl" />

          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            {/* 左侧分数 - 使用与排行榜一致的 ScoreRing 样式 */}
            <div className="flex items-center gap-6">
              <div className="relative" style={{ width: 128, height: 128 }}>
                {/* 外发光效果 */}
                <div
                  className={`absolute inset-0 rounded-full blur-md opacity-50 bg-gradient-to-br ${scoreInfo.gradient}`}
                />

                <svg
                  className="w-full h-full transform -rotate-90 relative z-10"
                  style={{ width: 128, height: 128 }}
                >
                  {/* 背景圆环 */}
                  <circle
                    cx={64}
                    cy={64}
                    r={60}
                    stroke="#1F2937"
                    strokeWidth={8}
                    fill="none"
                  />
                  {/* 进度圆环 */}
                  <circle
                    cx={64}
                    cy={64}
                    r={60}
                    stroke={scoreInfo.color}
                    strokeWidth={8}
                    fill="none"
                    strokeDasharray={2 * Math.PI * 60}
                    strokeDashoffset={
                      2 * Math.PI * 60 - (totalScore / 100) * 2 * Math.PI * 60
                    }
                    strokeLinecap="round"
                    className="transition-all duration-1000 ease-out"
                  />
                </svg>

                {/* 中心内容 */}
                <div className="absolute inset-0 flex flex-col items-center justify-center z-20">
                  <span
                    className={`text-4xl font-bold leading-none ${scoreInfo.textColor}`}
                  >
                    {Math.ceil(totalScore)}
                  </span>
                  <span className="text-xs text-slate-400 mt-1">综合评分</span>
                </div>
              </div>
              <div>
                <div
                  className={`text-2xl font-bold bg-gradient-to-r ${scoreInfo.gradient} bg-clip-text text-transparent mb-1`}
                >
                  {scoreInfo.label}
                </div>
                <p className="text-slate-400 text-sm">本次面试表现评估</p>
              </div>
            </div>

            {/* 右侧维度分数 */}
            <div className="flex-1 grid grid-cols-2 md:grid-cols-3 gap-4 w-full">
              {dimensions.map((item) => (
                <div
                  key={item.label}
                  className="bg-slate-800/50 rounded-xl p-4 text-center border border-slate-700/30"
                >
                  <item.icon className="w-5 h-5 text-amber-400 mx-auto mb-2" />
                  <div
                    className={`text-2xl font-bold mb-1 ${getScoreInfo(item.score).textColor}`}
                  >
                    {item.score}
                  </div>
                  <div className="text-xs text-slate-400">{item.label}</div>
                  <div className="mt-2 h-1 bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-1000 ${getScoreBarColor(item.score)}`}
                      style={{ width: `${item.score}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 详细分析 */}
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/30 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-emerald-600 flex items-center justify-center">
              <Target className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-white">详细能力分析</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {dimensions.map((item) => (
              <div
                key={item.key}
                className="bg-[#0B0F19]/50 rounded-xl p-4 border border-slate-700/30"
              >
                <div className="flex items-center gap-2 mb-2">
                  <item.icon className="w-4 h-4 text-amber-400" />
                  <h3 className="text-amber-400 font-semibold text-sm">
                    {item.label}
                  </h3>
                  <span
                    className={`ml-auto font-bold ${getScoreInfo(item.score).textColor}`}
                  >
                    {item.score}分
                  </span>
                </div>
                <p className="text-slate-300 text-sm leading-relaxed">
                  {detailedAnalysis[item.key as keyof typeof detailedAnalysis]}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* 行为表现分析 */}
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/30 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center">
              <User className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-white">行为表现分析</h2>
          </div>

          {report.behaviorAnalysis ? (
            <>
              {/* 数据内容 - 现有部分，保持不变 */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {/* 自信度 */}
                <div className="bg-[#0B0F19]/50 rounded-xl p-4 text-center border border-slate-700/30">
                  <div
                    className={`text-2xl font-bold mb-1 ${getBehaviorColor(report.behaviorAnalysis.averageConfidence)}`}
                  >
                    {report.behaviorAnalysis.averageConfidence}
                  </div>
                  <div className="text-xs text-slate-400">平均自信度</div>
                </div>

                {/* 紧张度 */}
                <div className="bg-[#0B0F19]/50 rounded-xl p-4 text-center border border-slate-700/30">
                  <div
                    className={`text-2xl font-bold mb-1 ${getBehaviorColor(report.behaviorAnalysis.averageNervousness, false)}`}
                  >
                    {report.behaviorAnalysis.averageNervousness}
                  </div>
                  <div className="text-xs text-slate-400">平均紧张度</div>
                </div>

                {/* 眼神接触 */}
                <div className="bg-[#0B0F19]/50 rounded-xl p-4 text-center border border-slate-700/30">
                  <div
                    className={`text-2xl font-bold mb-1 ${getBehaviorColor(Math.round(report.behaviorAnalysis.eyeContactRate * 100))}`}
                  >
                    {Math.round(report.behaviorAnalysis.eyeContactRate * 100)}%
                  </div>
                  <div className="text-xs text-slate-400">眼神接触率</div>
                </div>

                {/* 投入度 */}
                <div className="bg-[#0B0F19]/50 rounded-xl p-4 text-center border border-slate-700/30">
                  <div
                    className={`text-2xl font-bold mb-1 ${getBehaviorColor(report.behaviorAnalysis.averageEngagement)}`}
                  >
                    {report.behaviorAnalysis.averageEngagement}
                  </div>
                  <div className="text-xs text-slate-400">平均投入度</div>
                </div>
              </div>

              {/* 详细指标 */}
              <div className="grid md:grid-cols-2 gap-4">
                {/* 面部表情 */}
                <div className="bg-[#0B0F19]/50 rounded-xl p-4 border border-slate-700/30">
                  <h3 className="text-emerald-400 font-semibold text-sm mb-3">
                    面部表情
                  </h3>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">微笑频率</span>
                      <span
                        className={getBehaviorColor(
                          Math.round(
                            report.behaviorAnalysis.smileFrequency * 100,
                          ),
                        )}
                      >
                        {Math.round(
                          report.behaviorAnalysis.smileFrequency * 100,
                        )}
                        %
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">看向镜头</span>
                      <span className="text-white">
                        {report.behaviorAnalysis.gazeStats.lookingAtCamera} 帧
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">看向别处</span>
                      <span className="text-white">
                        {report.behaviorAnalysis.gazeStats.lookingAway} 帧
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">低头</span>
                      <span className="text-white">
                        {report.behaviorAnalysis.gazeStats.lookingDown} 帧
                      </span>
                    </div>
                  </div>
                </div>

                {/* 身体姿态 */}
                <div className="bg-[#0B0F19]/50 rounded-xl p-4 border border-slate-700/30">
                  <h3 className="text-emerald-400 font-semibold text-sm mb-3">
                    身体姿态
                  </h3>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">前倾占比</span>
                      <span
                        className={getBehaviorColor(
                          Math.round(
                            report.behaviorAnalysis.postureStats
                              .forwardLeanRatio * 100,
                          ),
                        )}
                      >
                        {Math.round(
                          report.behaviorAnalysis.postureStats
                            .forwardLeanRatio * 100,
                        )}
                        %
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">抱臂占比</span>
                      <span
                        className={getBehaviorColor(
                          Math.round(
                            report.behaviorAnalysis.postureStats
                              .armsCrossedRatio * 100,
                          ),
                          false,
                        )}
                      >
                        {Math.round(
                          report.behaviorAnalysis.postureStats
                            .armsCrossedRatio * 100,
                        )}
                        %
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">坐姿稳定性</span>
                      <span
                        className={getBehaviorColor(
                          Math.round(
                            report.behaviorAnalysis.postureStats
                              .averageStability * 100,
                          ),
                        )}
                      >
                        {Math.round(
                          report.behaviorAnalysis.postureStats
                            .averageStability * 100,
                        )}
                        %
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* AI 建议 */}
              <div className="mt-4 bg-gradient-to-r from-emerald-600/10 via-emerald-600/10 to-emerald-600/10 border border-emerald-500/20 rounded-xl p-4">
                <div className="space-y-1.5">
                  {report.behaviorAnalysis.suggestions?.map((suggestion, i) => (
                    <p key={i} className="text-emerald-300 text-sm">
                      💡 {suggestion}
                    </p>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-slate-500">
              <User className="w-12 h-12 mb-3 text-slate-600" />
              <p className="text-sm">本次面试未采集到行为表现数据</p>
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* 优势分析 */}
          <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/30">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-xl font-bold text-white">你的优势</h2>
            </div>
            <ul className="space-y-3">
              {strengths.map((strength: string, index: number) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center text-sm font-bold">
                    {index + 1}
                  </span>
                  <span className="text-slate-300 text-sm leading-relaxed">
                    {strength}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* 改进建议 */}
          <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/30">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-emerald-600 flex items-center justify-center">
                <Lightbulb className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-xl font-bold text-white">提升建议</h2>
            </div>
            <div className="space-y-4">
              {suggestions.map(
                (
                  suggestion: { category: string; content: string },
                  index: number,
                ) => (
                  <div
                    key={index}
                    className="bg-[#0B0F19]/50 rounded-xl p-4 border-l-4 border-amber-500"
                  >
                    <h3 className="text-amber-400 font-semibold text-sm mb-2">
                      {suggestion.category}
                    </h3>
                    <p className="text-slate-300 text-sm leading-relaxed">
                      {suggestion.content}
                    </p>
                  </div>
                ),
              )}
            </div>
          </div>
        </div>

        {/* 下一步行动 */}
        <div className="mt-6 bg-slate-800/50 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/30">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <Target className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-white">下一步行动</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {nextSteps.map((step: string, index: number) => (
              <div
                key={index}
                className="bg-[#0B0F19]/50 rounded-xl p-4 border border-slate-700/30"
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center text-sm font-bold">
                    {index + 1}
                  </span>
                  <span className="text-amber-400 font-semibold text-sm">
                    行动项
                  </span>
                </div>
                <p className="text-slate-300 text-sm leading-relaxed">{step}</p>
              </div>
            ))}
          </div>
        </div>

        {/* 底部提示 */}
        <div className="mt-6 bg-gradient-to-r from-amber-600/10 via-emerald-600/10 to-amber-600/10 border border-amber-500/20 rounded-xl p-4">
          <p className="text-amber-300 text-sm text-center">
            💡 提示：本报告由 AI
            智能生成，仅供参考。建议结合实际情况针对性提升，持续练习是进步的关键！
          </p>
        </div>

        {/* 操作按钮 - 从面试历史进入时隐藏 */}
        {!showBackButton && (
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
            <button
              onClick={onRestart}
              className="w-[300px] py-4 bg-gradient-to-r from-emerald-600 via-emerald-500 to-emerald-400 hover:from-emerald-500 hover:via-emerald-400 hover:to-emerald-300
                text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer"
            >
              <RotateCcw className="w-5 h-5" />
              再来一次面试
            </button>
            <button
              onClick={handleGoToSalaryNegotiation}
              className="w-[300px] py-4 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500
                text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer"
            >
              <span
                className="text-xl font-bold"
                style={{ transform: 'translateY(-2px)' }}
              >
                ¥
              </span>
              进入薪酬沟通
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
